import {
  IconArrowsSort,
  IconBook,
  IconFilter2,
  IconX,
} from "@tabler/icons-react";
import {
  default as CategoryFetch,
  default as FetchCategory,
} from "../components/category/fetch/FetchCategory";
import ShopNavigation from "../components/category/navigation/CategoryNavigation.tsx";
import CompactToggle from "../components/core/CompactToggle.tsx";
import LoadingInput from "../components/core/loading/LoadingInput";
import PageButton from "../components/core/PageSelection/PageButton";
import PageSelect from "../components/core/PageSelection/PageSelect";
import PageSize from "../components/core/PageSelection/PageSize";
import PopUp, { closeEveryModal, openModal } from "../components/core/PopUp";
import SEO from "../components/core/SEO.tsx";
import SearchBar from "../components/core/SearchBar";
import Footer from "../components/Footer";
import FilterContainer from "../components/filter/FilterContainer.tsx";
import ItemLayoutContainer from "../components/item/page/ItemLayoutContainer";
import SortOrderController from "../components/item/sort/SortOrderController.tsx";
import SortSelect from "../components/item/sort/SortSelect.tsx";
import LoginModal from "../components/login/LoginModal.tsx";
import { ROUTE_SHOP } from "../const/routes.ts";
import { useActiveParams } from "../hooks/useActiveParams.ts";
import { useSearchParamsHandler } from "../hooks/useSearchParamsHandler.ts";

const FILTER_ICON = <IconFilter2 className="inline-block size-4" />;
const SORT_ICON = <IconArrowsSort className="inline-block size-4" />;

const CategorySearchBar = () => {
  const { categorySlug } = useActiveParams();
  return (
    <FetchCategory
      slug={categorySlug}
      fallback={<LoadingInput classname="w-full input-sm" />}
    >
      {(category) => <SearchBar placeholder={`Durchsuche ${category.name}`} />}
    </FetchCategory>
  );
};

const openFilterModal = () => {
  openModal("shop-filter");
};
const openSortModal = () => {
  openModal("shop-sorting");
};

const CloseModalButton = () => (
  <button
    type="button"
    className="btn btn-success w-full"
    onClick={closeEveryModal}
  >
    Fertig
  </button>
);

const ShopOptionsLine = () => {
  const { isActive, remove, get } = useSearchParamsHandler("filter");
  const active = get().length;
  return (
    <div className="flex justify-between w-full mx-1">
      <div className="join gap-1  w-full">
        <button
          type="button"
          className="btn btn-accent btn-sm join-item"
          onClick={openFilterModal}
          aria-haspopup="dialog"
          aria-label={
            active > 0 ? `${active} aktive Filter, ändern` : "Filter öffnen"
          }
        >
          {FILTER_ICON}{" "}
          {active > 0 && `${active} aktive${active === 1 ? "r" : ""}   `} Filter
        </button>
        {isActive() && (
          <button
            type="button"
            className="btn btn-accent btn-sm join-item"
            onClick={() => remove()}
          >
            <IconX />
          </button>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sm join-item"
          onClick={openSortModal}
        >
          {SORT_ICON} Sortieren
        </button>
      </div>
      <CompactToggle
        className="btn btn-sm btn-ghost"
        listText="Kompakt"
        cardText="Locker"
      />
    </div>
  );
};

const SearchBarSection = () => (
  <div className="flex flex-col gap-2">
    <CategorySearchBar />
    <div className="flex">
      <ShopOptionsLine />
    </div>
    <div className="2xl:divider hidden"></div>
  </div>
);
const PopUpPagesTitle = (
  <>
    <IconBook />
    Seitenauswahl
  </>
);
const FilterPopUpTitle = (
  <>
    {FILTER_ICON}
    <label className="text-md">Filtern</label>
  </>
);
const SortPopUpTitle = (
  <>
    {SORT_ICON}
    <label className="text-md">Sortieren</label>
  </>
);
const NavSection = () => (
  <nav aria-label="Kategorien" className="flex flex-col gap-2 grow">
    <ShopNavigation routeTo={ROUTE_SHOP} />
  </nav>
);
const ItemSection = () => (
  <section aria-label="Suchergebnisse" className="flex flex-col gap-2 grow ">
    <div className="min-h-screen">
      <ItemLayoutContainer />
    </div>
    <div>
      <div className="w-full flex justify-center justify-self-end p-4 ">
        <PageButton />
      </div>
    </div>
  </section>
);
const PageSeo = () => {
  const { categorySlug } = useActiveParams();
  return (
    <FetchCategory
      slug={categorySlug}
      fallback={
        <SEO
          title="Windsurfmaterial gebraucht kaufen"
          description="Windsurfmaterial gebraucht kaufen & verkaufen – Segel, Boards, Masten, Gabelbäume."
          url={ROUTE_SHOP(categorySlug)}
        />
      }
    >
      {(category) => (
        <SEO
          title={`${category.name} gebraucht kaufen`}
          description={`Gebrauchte ${category.name} kaufen & verkaufen. Windsurfmaterial direkt von Surfern – kostenlos inserieren.`}
          url={ROUTE_SHOP(categorySlug)}
        />
      )}
    </FetchCategory>
  );
};
function ShopPage() {
  const { categorySlug } = useActiveParams();
  return (
    <div className="safe-bottom-with-dock safe-top-with-hanger">
      <PageSeo />
      <LoginModal />
      <PopUp id="shop-filter" title={FilterPopUpTitle} maxWidth="max-w-none">
        <div className="flex flex-col gap-2 py-2">
          <FilterContainer />
          <CloseModalButton />
        </div>
      </PopUp>
      <PopUp id="shop-sorting" title={SortPopUpTitle}>
        <div className="flex gap-2 py-2">
          <SortSelect />
          <SortOrderController />
        </div>
        <CloseModalButton />
      </PopUp>
      <PopUp id="pages" title={PopUpPagesTitle}>
        <div className="flex w-full justify-between items-center">
          <div className="flex items-center justify-around w-full">
            <label className="text-xl opacity-70">
              Wähle Anzahl pro Seite aus:
            </label>
            <div>
              <PageSize />
            </div>
          </div>
        </div>
        <div className="divider" />
        <form method="dialog">
          <PageSelect />
        </form>
      </PopUp>

      {/* Main Content */}

      <main className="flex 2xl:flex-row flex-col justify-around p-page">
        <NavSection />
        <h1 className="sr-only">
          <CategoryFetch slug={categorySlug} fallback="Kategorie">
            {(category) => `${category.name} kaufen – Surfanzeigen`}
          </CategoryFetch>
        </h1>
        <div className="divider flex 2xl:divider-horizontal px-4"></div>
        <div className="flex flex-col 2xl:min-w-3/4 gap-8">
          <SearchBarSection />
          <ItemSection />
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default ShopPage;
